import React from "react";
import { Link } from "react-router-dom";
import { Grid, PlainButton, Tag } from "@culturehq/components";

import { useProfile } from "app/context";
import { useFieldsLabels, useHasBecomeVisible } from "app/hooks";
import { canViewCompaniesField, canViewSkillsField } from "app/permissions";
import CompanyLink from "app/links/CompanyLink";
import { ClassYearLink, ClubLink, DepartmentLink, InterestLink, LocationLink, ProgramLink, ProfileLink, DegreeLink, SkillLink } from "app/links";
import { Trans } from "app/utils";

import BuildConnection from "components/person/BuildConnection";
import UserCommonalities from "components/community/UserCommonalities";
import ImageLightbox from "components/display/ImageLightbox";

import getUserName from "app/utils/getUserName";
import QuickLinks from "./QuickLinks";

import styles from "./styles.module.scss";

const PersonHeaderImage = ({ user }) => (
  <ImageLightbox image={user.avatar}>
    {onTrigger => (
      <PlainButton
        className="person-image-wrapper"
        style={{ backgroundImage: `url(${user.avatar.mediumUrl})` }}
        onClick={onTrigger}
      />
    )}
  </ImageLightbox>
);

const PersonHeaderLink = ({ user }) => (
  <ProfileLink
    ariaLabel={getUserName(user)}
    user={user}
    className="person-image-wrapper"
    style={{ backgroundImage: `url(${user.avatar.mediumUrl})` }}
  />
);

const PersonHeader = ({ onChange, onProfile = true, user }) => {
  const { profile } = useProfile();
  const labels = useFieldsLabels();

  const { organization } = profile;
  const { name } = organization;

  const [lazyElementRef, _] = useHasBecomeVisible();

  return (
    <div ref={lazyElementRef}>
      <Grid>
        <Grid.Item sm={4} lg={3} className="person-image Person--header">
          {
            onProfile
              ? <PersonHeaderImage user={user} />
              : <PersonHeaderLink user={user} />
          }
          {user.points > 0 && (
            <div className="PointsBadge">
              <Link to="/score">
                <span>{user.points}</span>
              </Link>
            </div>
          )}
        </Grid.Item>
        <Grid.Item sm={8} lg={9}>
          <div className="Person--buttons">
            {user.connection && user.id !== profile.id && (
              <BuildConnection
                profile={user}
              />
            )}
            <QuickLinks onChange={onChange} profile={profile} user={user} />
          </div>
          <h1>
            {getUserName(user)}{" "}
            {!user.claimed && (
              <>{" "}<Tag color="gray"><Trans>Invited</Trans></Tag></>
            )}
            {user.id !== profile.id && (
              <span className={styles.common}>
                <UserCommonalities connection={user.connection} user={user} />
              </span>
            )}
          </h1>
          <h2>{user.title}</h2>
          <p>
            <a className="link" href={`mailto:${user.email}`}>
              {user.email}
            </a>
          </p>
          <p>
            {user.location && (
              <span>
                <Trans>Location</Trans>:{" "}
                <LocationLink location={user.location} />
              </span>
            )}
          </p>
          {!profile.organization.isEducational && (
            <p>
              <Trans>{`${labels.departments.singular}:`}{" "}</Trans>
              {user.departments.map((department, index) => (
                <span key={department.id}>
                  <DepartmentLink department={department} />
                  {index !== user.departments.length - 1 && ", "}
                </span>
              ))}
            </p>
          )}
          {canViewCompaniesField(profile.organization) && (
            <p>
              <Trans>{labels.companies.singular}</Trans>:{" "}
              {user.companies.map((company, index) => (
                <span key={company.id}>
                  <CompanyLink company={company} />
                  {index !== user.companies.length - 1 && ", "}
                </span>
              ))}
            </p>
          )}
          {profile.organization.isEducational && (
            <>
              <p>
                <Trans>Program</Trans>:{" "}
                {user.programs.map((program, index) => (
                  <span key={program.id}>
                    <ProgramLink program={program} />
                    {index !== user.programs.length - 1 && ", "}
                  </span>
                ))}
              </p>
              <p>
                {user.classYear && (
                  <span>
                    <Trans>{name === "Dartmouth Guarini" ? "Years in Program" : "Class Year"}</Trans>:{" "}
                    <ClassYearLink classYear={user.classYear} />
                  </span>
                )}
              </p>
              <p>
                {user.degree && (
                  <span>
                    <Trans>Degree</Trans>:{" "}
                    <DegreeLink degree={user.degree} />
                  </span>
                )}
              </p>
              <p>
                <Trans>Clubs</Trans>:{" "}
                {user.clubs.map((club, index) => (
                  <span key={club.id}>
                    <ClubLink club={club} />
                    {index !== user.clubs.length - 1 && ", "}
                  </span>
                ))}
              </p>
            </>
          )}
          <p>
            <Trans>Interests</Trans>:{" "}
            {user.interests?.map((interest, index) => (
              <span key={interest.id}>
                <InterestLink interest={interest} />
                {index !== user.interests.length - 1 && ", "}
              </span>
            ))}
          </p>
          {canViewSkillsField(organization) && !profile.organization.isEducational && (
            <p>
              <Trans>Skills</Trans>:{" "}
              {user.skills?.map((skill, index) => (
                <span key={skill.id}>
                  <SkillLink skill={skill} />
                  {index !== user.skills.length - 1 && ", "}
                </span>
              ))}
            </p>
          )}
        </Grid.Item>
      </Grid>
    </div>
  );
};

export default PersonHeader;
